import { useState } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface AdaptiveImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  /** Priority loading for above-the-fold images */
  priority?: boolean;
  /** Optional width for sizing hints */
  width?: number;
  /** Optional height for sizing hints */
  height?: number;
}

const FALLBACK_IMAGE = "/placeholder.svg";

const AdaptiveImage = ({ 
  src, 
  alt, 
  className, 
  containerClassName,
  priority = false,
  width,
  height
}: AdaptiveImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setIsLandscape(img.naturalWidth >= img.naturalHeight);
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const imageSrc = hasError ? FALLBACK_IMAGE : src;

  return (
    <div className={cn("relative flex items-center justify-center overflow-hidden", containerClassName)}>
      {!isLoaded && (
        <Skeleton className="absolute inset-0" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        width={width}
        height={height}
        className={cn(
          "transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0",
          isLandscape ? "w-full h-auto" : "h-full w-auto",
          className
        )}
      />
    </div>
  );
};

export { AdaptiveImage };
